import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { asyncAction } from "@client/lib/actions";
import { getUnwrapResponse, unwrapResponse } from "@client/lib/api";
import { getCurrentUserId, getReferringUserId } from "@client/lib/cookie";
import { TargetSelector, getSettingValueSelector } from "@client/selectors";
import { type BaseAppState, type Common, type EntityRef, Status } from "@client/types";
import { type HttpApiResponse } from "@client/types/Api";
import appInfo, { isStaffOrSuperUser } from "@common/appInfo";
import { type Dispatch } from "redux";
import { initAsyncAction } from "../actions";
import api from "./api";
import { getDummyItem, mapIncentiveApiItem, mapIncentiveApiReward, mapLaunchApiItem, mapLaunchApiReward } from "./lib";
import { localStateSelector, makeSourceIdSelector } from "./selectors";
import { type Leaderboard } from "./types";

export enum Actions {
  ApplyNow = "LEADERBOARD_APPLY_NOW",

  ApplyBegin = "LEADERBOARD_APPLY_BEGIN",
  ApplyFailure = "LEADERBOARD_APPLY_FAILURE",
  ApplySuccess = "LEADERBOARD_APPLY_SUCCESS",

  InitBegin = "LEADERBOARD_INIT_BEGIN",
  InitFailure = "LEADERBOARD_INIT_FAILURE",
  InitSuccess = "LEADERBOARD_INIT_SUCCESS",

  LoadPageBegin = "LEADERBOARD_LOAD_PAGE_BEGIN",
  LoadPageFailure = "LEADERBOARD_LOAD_PAGE_FAILURE",
  LoadPageSuccess = "LEADERBOARD_LOAD_PAGE_SUCCESS",

  SetShowingModal = "LEADERBOARD_SET_SHOWING_MODAL",
  ShowProfile = "LEADERBOARD_SHOW_PROFILE",

  SubmitVoteBegin = "LEADERBOARD_SUBMIT_VOTE_BEGIN",
  SubmitVoteFailure = "LEADERBOARD_SUBMIT_VOTE_FAILURE",
  SubmitVoteSuccess = "LEADERBOARD_SUBMIT_VOTE_SUCCESS",

  UpdateProfilePicture = "LEADERBOARD_UPDATE_PROFILE_PICTURE"
}

export type ApplyBeginActionData = EntityRef & {
  launchId: string;
  timeZone: string;
  user: Leaderboard.View.UserData | null;
};
export type ApplySuccessActionData = EntityRef & {
  item: Leaderboard.Item;
};
export const _apply = asyncAction<BaseAppState, ApplyBeginActionData, HttpApiResponse<Leaderboard.Api.ApplyResponse>>({
  types: {
    begin: Actions.ApplyBegin,
    failure: Actions.ApplyFailure,
    success: [({ res, dispatch, params }) => dispatch(onApplied(params, unwrapResponse(res)))]
  },
  action: (state: BaseAppState, dispatch: any, params: ApplyBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.apply(apiInfo, params);
  }
});

export const apply =
  (ref: EntityRef, user: Leaderboard.View.UserData | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), ref);

    if (localState.status === "Ready") {
      return dispatch(
        _apply({
          ...ref,
          launchId: localState.sourceId!,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          user
        })
      );
    }
  };

export const onApplied = (ref: EntityRef, data: Leaderboard.Api.LaunchLeadboardItem) => ({
  type: Actions.ApplySuccess,
  data: <ApplySuccessActionData>{
    ...ref,
    item: mapLaunchApiItem(data)
  }
});

export const applyNow = (ref: EntityRef) => async (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  // const state = getState();
  //const localState = localStateSelector(state, {id});
  if (getCurrentUserId() || !!appInfo.user) {
    dispatch(apply(ref, null));
    // const apiInfo = makeModuleApiInfoSelector()(state, {id});

    // const res = await api.apply(apiInfo, localState.sourceId!);
    // if (res.error) throw new Error(res.error);

    // const item = await api.getUserLaunchLeaderboard(apiInfo, localState.sourceId!, getCurrentUserId())
  } else {
    dispatch(setShowingModal(ref, { ref: "", type: "Apply" }));
  }
};

export type LoadPageBeginActionData = EntityRef & {
  page: number;
};
export type LoadPageSuccessActionData = {
  items: Leaderboard.Item[];
  hasMore: boolean;
  page: number;
  pageSize: number;
};
export const loadPage = asyncAction<BaseAppState, LoadPageBeginActionData, LoadPageSuccessActionData>({
  types: {
    begin: Actions.LoadPageBegin,
    failure: Actions.LoadPageFailure,
    success: Actions.LoadPageSuccess
  },
  action: async (
    state: BaseAppState,
    dispatch: any,
    params: LoadPageBeginActionData
  ): Promise<LoadPageSuccessActionData> => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    const pageSize = getSettingValueSelector(TargetSelector.Entity, "pageSize")(state, params);
    const type = getSettingValueSelector(TargetSelector.Entity, "type")(state, params);
    if (type === "launch") {
      const launchId = getSettingValueSelector(TargetSelector.Entity, "launchId")(state, params);
      if (launchId) {
        const ll = await getUnwrapResponse(api.getLaunchItems)(apiInfo, launchId, params.page, pageSize);

        return {
          hasMore: ll.length === pageSize,
          items: ll.map(mapLaunchApiItem),
          page: params.page,
          pageSize
        };
      }
    }

    if (type === "incentive") {
      const incentiveId = getSettingValueSelector(TargetSelector.Entity, "incentiveId")(state, params);
      if (incentiveId) {
        const ll = await getUnwrapResponse(api.getIncentiveItems)(apiInfo, incentiveId, params.page, pageSize);

        return {
          hasMore: ll.length === pageSize,
          items: ll.map(mapIncentiveApiItem),
          page: params.page,
          pageSize
        };
      }
    }

    return {
      hasMore: false,
      items: [],
      page: 1,
      pageSize
    };
  }
});

export const loadNextPage = (args: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  dispatch(loadPage({ ...args, page: localState.page + 1 }));
};

export type InitBeginActionData = EntityRef & {
  sourceId: string | null;
};
export type InitSuccessActionData = {
  current: Leaderboard.Item | null;

  countryCode: string | null;
  referring: Leaderboard.Item | null;
  rewards: Leaderboard.Reward[];
  sourceId: string | null;
  source: Leaderboard.Launch | null;
};
export const _init = initAsyncAction<BaseAppState, InitBeginActionData, InitSuccessActionData>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: [
      Actions.InitSuccess,
      ({ dispatch, params }) => dispatch(loadPage({ id: params.id, source: params.source, page: 1 }))
    ]
  },
  action: async (state: BaseAppState, dispatch: any, params: InitBeginActionData): Promise<InitSuccessActionData> => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    const type = getSettingValueSelector(TargetSelector.Entity, "type")(state, params);
    if (type === "launch") {
      const launchId = getSettingValueSelector(TargetSelector.Entity, "launchId")(state, params);
      if (launchId) {
        const launch = await getUnwrapResponse(api.getLaunch)(apiInfo, launchId);

        return {
          current: launch.current ? mapLaunchApiItem(launch.current) : null,
          countryCode: appInfo.countryCode,
          referring: launch.referring ? mapLaunchApiItem(launch.referring) : null,
          rewards: launch.rewards.map(mapLaunchApiReward),
          source: launch.launch,
          sourceId: params.sourceId
        };
      }
    }

    if (type === "incentive") {
      const incentiveId = getSettingValueSelector(TargetSelector.Entity, "incentiveId")(state, params);
      if (incentiveId) {
        const incentive = await getUnwrapResponse(api.getIncentive)(apiInfo, incentiveId);

        return {
          current: incentive.current ? mapIncentiveApiItem(incentive.current) : null,
          countryCode: appInfo.countryCode,
          referring: incentive.referring ? mapIncentiveApiItem(incentive.referring) : null,
          rewards: incentive.rewards.map(mapIncentiveApiReward),
          source: null,
          sourceId: params.sourceId
        };
      }
    }

    return {
      current: null,

      countryCode: null,
      referring: null,
      rewards: [],
      source: null,
      sourceId: params.sourceId
    };
  }
});

export const init = (args: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  const sourceId = makeSourceIdSelector()(state, args);

  if (localState.status === Status.None && getReferringUserId()) {
    dispatch(setShowingModal(args, { ref: "", type: "Vote" }));
  }
  if (localState.status === Status.None || (localState.status !== Status.Loading && localState.sourceId !== sourceId)) {
    dispatch(_init({ ...args, sourceId: sourceId }));
  }
};

export type SetShowingModalActionData = EntityRef & {
  modal: Leaderboard.ShowingModal | null;
};
export const setShowingModal = (ref: EntityRef, modal: Leaderboard.ShowingModal | null) => ({
  type: Actions.SetShowingModal,
  data: {
    ...ref,
    modal
  }
});

export type ShowProfileActionData = EntityRef & {
  hasVotedNow: boolean;
};
export const showProfile = (ref: EntityRef) => ({
  type: Actions.ShowProfile,
  data: {
    ...ref,
    hasVotedNow: false
  }
});

export const showProfileAfterVote = (ref: EntityRef) => ({
  type: Actions.ShowProfile,
  data: {
    ...ref,
    hasVotedNow: true
  }
});

export const showVoteModal =
  (ref: EntityRef, userId: number) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), ref);

    if (localState.current?.userId === userId) {
      dispatch(showProfile(ref));
    } else {
      dispatch(
        setShowingModal(ref, {
          ref: userId,
          type: "Vote"
        })
      );
    }
  };

export type SubmitVoteBeginActionData = EntityRef & {
  launchId: string;
  timeZone: string;
  userId: number;
  votingUser: Leaderboard.View.UserData | null;
};
export type SubmitVoteSuccessActionData = {
  current: Leaderboard.Item | null;
  item: Leaderboard.Item;
};
export const _submitVote = asyncAction<BaseAppState, SubmitVoteBeginActionData, SubmitVoteSuccessActionData>({
  types: {
    begin: Actions.SubmitVoteBegin,
    failure: Actions.SubmitVoteFailure,
    success: [
      Actions.SubmitVoteSuccess,
      ({ dispatch, params }) => dispatch(loadPage({ id: params.id, source: params.source, page: 1 }))
    ]
  },
  action: async (
    state: BaseAppState,
    dispatch: any,
    params: SubmitVoteBeginActionData
  ): Promise<SubmitVoteSuccessActionData> => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    const res: HttpApiResponse<Leaderboard.Api.SubmitVoteResponse> = await (isStaffOrSuperUser()
      ? Promise.resolve({
          status: 200,
          response: {
            status: "success",
            data: {
              current: null,
              voted: getDummyItem()
            }
          }
        })
      : api.submitVote(apiInfo, params));
    const data = unwrapResponse(res);

    const { current, voted } = data;
    return {
      current: current ? mapLaunchApiItem(current) : null,
      item: mapLaunchApiItem(voted)
    };
  }
});

export const submitVote =
  (ref: EntityRef, data: Leaderboard.View.UserData | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const localState = localStateSelector(getState(), { id: ref.id });

    if (localState.status === "Ready") {
      dispatch(
        _submitVote({
          ...ref,
          launchId: localState.sourceId!,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          userId: localState.vote!.item!.userId,
          votingUser: data
        })
      );
    }
  };

export type UpdateProfilePictureSuccessActionData = EntityRef & {
  media: Common.Media | null;
};
export const updateProfilePicture = (ref: EntityRef, media: Common.Media | null) => ({
  type: Actions.UpdateProfilePicture,
  data: {
    ...ref,
    media
  }
});
